// apps/site-dna-web/src/components/ev/OptimizationResults.js
/**
 * OptimizationResults - Display LP optimization results
 *
 * Shows the results from the HiGHS-based EV charging optimizer including:
 * - Key metrics (peak demand, energy, cost breakdown)
 * - Load profile chart
 * - Flexibility windows for multi-agent negotiation
 * - Per-vehicle charging schedules
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    CircularProgress,
    Alert,
    Collapse,
    IconButton,
    Divider,
    Tooltip,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    alpha,
} from '@mui/material';
import {
    Bolt as OptimizeIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    AttachMoney as CostIcon,
    Speed as PeakIcon,
    Battery80 as EnergyIcon,
    Schedule as FlexIcon,
    DirectionsCar as VehicleIcon,
    Today as DayIcon,
    CalendarMonth as YearIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import EVLoadChart from './EVLoadChart';
import { vortxColors, SectionCard, useResponsive } from '@vortx/shared-ui';

/**
 * MetricCard - Displays a single optimization metric
 */
function MetricCard({ label, value, unit, subtitle, color = vortxColors.teal, tooltip }) {
    const card = (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                bgcolor: alpha(color, 0.1),
                border: `1px solid ${alpha(color, 0.3)}`,
                borderRadius: 1,
                textAlign: 'center',
                height: '100%',
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ color, fontWeight: 700 }}>
                {value}
            </Typography>
            {unit && (
                <Typography variant="caption" color="text.secondary">
                    {unit}
                </Typography>
            )}
            {subtitle && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {subtitle}
                </Typography>
            )}
        </Paper>
    );

    if (tooltip) {
        return <Tooltip title={tooltip} arrow>{card}</Tooltip>;
    }
    return card;
}

/**
 * DetailSection - A collapsible section for detailed results
 */
function DetailSection({ title, icon, children, defaultExpanded = false }) {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <Paper
            elevation={0}
            sx={{
                bgcolor: alpha(vortxColors.teal, 0.03),
                border: `1px solid ${alpha(vortxColors.teal, 0.1)}`,
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(vortxColors.teal, 0.05) },
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {icon}
                    <Typography variant="subtitle2" fontWeight={600}>
                        {title}
                    </Typography>
                </Box>
                <IconButton size="small">
                    {expanded ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
            </Box>
            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ p: 2 }}>
                    {children}
                </Box>
            </Collapse>
        </Paper>
    );
}

/**
 * DetailRow - A single metric row in detail sections
 */
function DetailRow({ label, value, highlight = false }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography
                variant="body2"
                fontWeight={highlight ? 600 : 400}
                color={highlight ? vortxColors.gold : 'text.primary'}
            >
                {value}
            </Typography>
        </Box>
    );
}

/**
 * Format currency value
 */
function formatCurrency(value) {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

/**
 * Format number with commas
 */
function formatNumber(value, decimals = 0) {
    if (value == null) return '-';
    return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

/**
 * OptimizationResults Component
 *
 * @param {Object} props
 * @param {Object} props.results - Optimization results from API
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @param {number} props.siteCapacity - Site capacity for chart reference
 * @param {Function} props.onRunOptimization - Callback to run optimization
 * @param {Object} props.scenario - Selected scenario (optional)
 */
function OptimizationResults({
    results = null,
    loading = false,
    error = null,
    siteCapacity = null,
    onRunOptimization,
    scenario = null,
}) {
    const { isMobile } = useResponsive();
    const [chartView, setChartView] = useState('day');

    const metrics = results?.metrics || {};
    const flexibility = results?.flexibility || {};
    const vehicleSchedules = results?.vehicle_schedules || {};
    const loads = results?.loads_kw || [];

    // Calculate daily average if we have annual data
    const dailyEnergy = metrics.total_energy_kwh ? metrics.total_energy_kwh / 365 : 0;

    return (
        <SectionCard
            title="LP Optimization"
            icon={<OptimizeIcon />}
            action={
                <Button
                    variant="contained"
                    size="small"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <OptimizeIcon />}
                    onClick={onRunOptimization}
                    disabled={loading}
                    sx={{
                        bgcolor: vortxColors.gold,
                        '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) },
                    }}
                >
                    {loading ? 'Optimizing...' : 'Run Optimization'}
                </Button>
            }
        >
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* No results state */}
            {!results && !loading && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <OptimizeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Optimization Results Yet
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                        Run the LP optimizer to find the minimum-cost charging schedule
                        using HiGHS linear programming. The optimizer considers TOU rates,
                        demand charges, and vehicle constraints.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<OptimizeIcon />}
                        onClick={onRunOptimization}
                        sx={{
                            bgcolor: vortxColors.gold,
                            '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) },
                        }}
                    >
                        Run Optimization
                    </Button>
                </Box>
            )}

            {/* Results display */}
            {results && (
                <>
                    {/* Key Metrics Grid */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <MetricCard
                                label="Peak Demand"
                                value={formatNumber(metrics.peak_demand_kw, 1)}
                                unit="kW"
                                color={vortxColors.teal}
                                tooltip="Maximum charging power across all hours"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard
                                label="Total Energy"
                                value={formatNumber(metrics.total_energy_kwh, 0)}
                                unit="kWh/year"
                                subtitle={`${formatNumber(dailyEnergy, 1)} kWh/day`}
                                color={vortxColors.gold}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard
                                label="Total Cost"
                                value={formatCurrency(metrics.total_cost)}
                                unit="/year"
                                color={vortxColors.teal}
                                tooltip="Energy cost + Demand charges"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard
                                label="Avg Rate"
                                value={`$${(metrics.avg_rate_per_kwh || 0).toFixed(3)}`}
                                unit="/kWh"
                                color={vortxColors.gold}
                                tooltip="Effective blended energy rate"
                            />
                        </Grid>
                    </Grid>

                    {/* Chart View Toggle */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <ToggleButtonGroup
                            value={chartView}
                            exclusive
                            onChange={(e, v) => v && setChartView(v)}
                            size="small"
                        >
                            <ToggleButton value="day">
                                <DayIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Day
                            </ToggleButton>
                            <ToggleButton value="year">
                                <YearIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Year
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Load Profile Chart */}
                    <Box sx={{ mb: 3 }}>
                        <EVLoadChart
                            loads={loads}
                            peakDemand={metrics.peak_demand_kw}
                            siteCapacity={siteCapacity}
                            view={chartView}
                            height={300}
                        />
                    </Box>

                    {/* Detailed Sections */}
                    <Grid container spacing={2}>
                        {/* Cost Breakdown */}
                        <Grid item xs={12} md={6}>
                            <DetailSection
                                title="Cost Breakdown"
                                icon={<CostIcon sx={{ color: vortxColors.gold, fontSize: 20 }} />}
                                defaultExpanded={true}
                            >
                                <DetailRow
                                    label="Energy Cost"
                                    value={formatCurrency(metrics.energy_cost)}
                                />
                                <DetailRow
                                    label="Demand Charges"
                                    value={formatCurrency(metrics.demand_cost)}
                                />
                                <Divider sx={{ my: 1 }} />
                                <DetailRow
                                    label="Total Annual Cost"
                                    value={formatCurrency(metrics.total_cost)}
                                    highlight
                                />

                                {/* Cost by TOU Period */}
                                {metrics.cost_by_period && Object.keys(metrics.cost_by_period).length > 0 && (
                                    <>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 2, mb: 1 }}
                                        >
                                            Cost by TOU Period
                                        </Typography>
                                        {Object.entries(metrics.cost_by_period).map(([period, cost]) => (
                                            <DetailRow
                                                key={period}
                                                label={period}
                                                value={formatCurrency(cost)}
                                            />
                                        ))}
                                    </>
                                )}
                            </DetailSection>
                        </Grid>

                        {/* Flexibility Windows */}
                        <Grid item xs={12} md={6}>
                            <DetailSection
                                title="Flexibility Windows"
                                icon={<FlexIcon sx={{ color: vortxColors.teal, fontSize: 20 }} />}
                                defaultExpanded={true}
                            >
                                <Box sx={{ mb: 2 }}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="caption">
                                            Flexibility windows show how much charging load can be shifted
                                            for multi-agent co-optimization with Site DNA.
                                        </Typography>
                                    </Alert>
                                </Box>

                                {flexibility.shiftable_load && flexibility.shiftable_load.length > 0 ? (
                                    <>
                                        <DetailRow
                                            label="Shiftable Windows"
                                            value={flexibility.shiftable_load.length}
                                        />
                                        <DetailRow
                                            label="Total Shiftable Load"
                                            value={`${formatNumber(
                                                flexibility.shiftable_load.reduce((sum, w) => sum + w.kw_shiftable, 0),
                                                1
                                            )} kWh`}
                                        />
                                        <Divider sx={{ my: 1 }} />
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 1 }}
                                        >
                                            Sample Shiftable Windows
                                        </Typography>
                                        {flexibility.shiftable_load.slice(0, 5).map((window, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 0.5,
                                                }}
                                            >
                                                <Chip
                                                    size="small"
                                                    label={`Hour ${window.hour % 24}`}
                                                    sx={{ minWidth: 70 }}
                                                />
                                                <Typography variant="caption">
                                                    {formatNumber(window.kw_shiftable, 1)} kW
                                                    (shift: {window.earliest_hour % 24}-{window.latest_hour % 24})
                                                </Typography>
                                            </Box>
                                        ))}
                                        {flexibility.shiftable_load.length > 5 && (
                                            <Typography variant="caption" color="text.secondary">
                                                ... and {flexibility.shiftable_load.length - 5} more windows
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No significant flexibility available. All charging is time-constrained.
                                    </Typography>
                                )}
                            </DetailSection>
                        </Grid>

                        {/* Vehicle Schedules */}
                        <Grid item xs={12}>
                            <DetailSection
                                title="Vehicle Schedules"
                                icon={<VehicleIcon sx={{ color: vortxColors.teal, fontSize: 20 }} />}
                                defaultExpanded={false}
                            >
                                {Object.keys(vehicleSchedules).length > 0 ? (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Vehicle</TableCell>
                                                    <TableCell align="right">Energy Delivered</TableCell>
                                                    <TableCell align="right">Charging Cost</TableCell>
                                                    <TableCell align="center">Window</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.values(vehicleSchedules).map((schedule) => (
                                                    <TableRow key={schedule.vehicle_id}>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {schedule.vehicle_id}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(schedule.energy_delivered_kwh, 1)} kWh
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatCurrency(schedule.charging_cost)}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                size="small"
                                                                label={`${schedule.start_hour}:00 - ${schedule.end_hour}:00`}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No vehicle schedule data available.
                                    </Typography>
                                )}
                            </DetailSection>
                        </Grid>
                    </Grid>
                </>
            )}
        </SectionCard>
    );
}

export default OptimizationResults;
